'use client';

import React, { useId } from 'react';
import './Toggle.css';

export type ToggleSize = 'sm' | 'md' | 'lg';

export interface ToggleProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> {
  /** Label text displayed next to toggle */
  label?: React.ReactNode;
  /** Position of the label relative to the toggle */
  labelPosition?: 'left' | 'right';
  /** Toggle size variant */
  size?: ToggleSize;
}

export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      label,
      labelPosition = 'right',
      size = 'md',
      checked,
      defaultChecked,
      disabled,
      className = '',
      id,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;

    const classes = [
      'hi-ui-toggle',
      `hi-ui-toggle--size-${size}`,
      labelPosition === 'left' && 'hi-ui-toggle--label-left',
      disabled && 'hi-ui-toggle--disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <label className={classes} htmlFor={inputId}>
        {label && labelPosition === 'left' && <span className="hi-ui-toggle__label">{label}</span>}
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          id={inputId}
          className="hi-ui-toggle__input"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          aria-checked={checked}
          aria-label={!label ? ariaLabel : undefined}
          aria-describedby={ariaDescribedBy}
          {...rest}
        />
        <span className="hi-ui-toggle__track" aria-hidden="true">
          <span className="hi-ui-toggle__thumb" />
        </span>
        {label && labelPosition === 'right' && <span className="hi-ui-toggle__label">{label}</span>}
      </label>
    );
  }
);

Toggle.displayName = 'Toggle';
